import React, { useEffect, useRef } from 'react';
import { useVisualViewportBox } from '../../hooks/useVisualViewport';
import { colors, radii, shadows, FONT } from '../../theme';
import { Phone, User, X, Check } from 'lucide-react';

export default function CompactCallBanner({ type, caller, label, onAnswer, onDecline }) {
  const touchStartY = useRef(null);
  const ringRef = useRef({ interval: null, ctx: null });
  // With the keyboard up WebKit scrolls the page; the banner follows the
  // visible top instead of the page's (useVisualViewport.js).
  const vvBox = useVisualViewportBox();

  useEffect(() => {
    let stopped = false; let ctx = null;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const playRing = () => {
        if (stopped || !ctx || ctx.state === 'closed') return;
        try { [0, 0.5].forEach(offset => {
          const osc = ctx.createOscillator(); const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination); osc.type = 'sine'; osc.frequency.value = 440;
          gain.gain.setValueAtTime(0.12, ctx.currentTime + offset);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.35);
          osc.start(ctx.currentTime + offset); osc.stop(ctx.currentTime + offset + 0.35);
          const osc2 = ctx.createOscillator(); const gain2 = ctx.createGain();
          osc2.connect(gain2); gain2.connect(ctx.destination); osc2.type = 'sine'; osc2.frequency.value = 480;
          gain2.gain.setValueAtTime(0.08, ctx.currentTime + offset);
          gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.35);
          osc2.start(ctx.currentTime + offset); osc2.stop(ctx.currentTime + offset + 0.35);
        }); } catch (e) {}
      };
      playRing(); ringRef.current.interval = setInterval(playRing, 2800); ringRef.current.ctx = ctx;
    } catch (e) {}
    return () => { stopped = true; clearInterval(ringRef.current.interval); if (ctx) ctx.close().catch(() => {}); };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (navigator.vibrate) {
      const v = () => navigator.vibrate([300, 200, 300, 1500]); v();
      const i = setInterval(v, 2300);
      return () => { clearInterval(i); navigator.vibrate(0); };
    }
  }, []);

  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = (e) => { if (touchStartY.current === null) return; if (touchStartY.current - e.changedTouches[0].clientY > 40) onDecline(); touchStartY.current = null; };

  return (
    <div style={vvBox.top ? { ...S.container, top: `calc(${vvBox.top}px + max(12px, env(safe-area-inset-top)))` } : S.container} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <div style={S.avatar}>{type === 'room' ? <User size={20} color="#ece9e6" /> : <Phone size={20} color="#ece9e6" />}</div>
      <div style={S.info}>
        <div style={S.callerName}>{caller}</div>
        <div style={S.label}>{label}</div>
      </div>
      <div style={S.buttons}>
        <button onClick={onDecline} style={S.declineBtn}><X size={16} color="#15141a" /></button>
        <button onClick={onAnswer} style={S.answerBtn}><Check size={16} color="#15141a" /></button>
      </div>
    </div>
  );
}

const S = {
  container: {
    position: 'fixed', top: 'max(12px, env(safe-area-inset-top))', left: 16, right: 16,
    zIndex: 200, background: 'rgba(21, 20, 26, 0.95)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    borderRadius: radii.lg, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
    boxShadow: shadows.xl, animation: 'bannerSlideDown 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)', fontFamily: FONT,
  },
  avatar: {
    width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.12)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0,
    border: `2px solid ${colors.greenDot}`, animation: 'bannerPulse 2s infinite',
  },
  info: { flex: 1, minWidth: 0 },
  callerName: { fontSize: 15, fontWeight: 600, color: '#ece9e6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  label: { fontSize: 13, color: 'rgba(255,255,255,0.6)' },
  buttons: { display: 'flex', gap: 8, flexShrink: 0 },
  declineBtn: { width: 36, height: 36, borderRadius: '50%', background: colors.red, border: 'none', color: colors.white, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  answerBtn: { width: 36, height: 36, borderRadius: '50%', background: colors.greenDot, border: 'none', color: colors.white, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
};
